import React from 'react';
import { XCircleIcon, AlertTriangleIcon, CheckCircleIcon, InfoIcon, BellIcon, SettingsIcon, XIcon } from 'lucide-react';
import { MessageType } from './MessagesManagement';
interface MessagePreviewProps {
  message: {
    type: MessageType;
    text: string;
  };
  displayType: 'banner' | 'toast' | 'inline';
}
export function MessagePreview({
  message,
  displayType
}: MessagePreviewProps) {
  const getIcon = () => {
    switch (message.type) {
      case 'Error':
        return <XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" data-id="element-17224" />;
      case 'Warning':
        return <AlertTriangleIcon className="h-5 w-5 text-amber-400" aria-hidden="true" data-id="element-17225" />;
      case 'Success':
        return <CheckCircleIcon className="h-5 w-5 text-green-400" aria-hidden="true" data-id="element-17226" />;
      case 'Info':
        return <InfoIcon className="h-5 w-5 text-blue-400" aria-hidden="true" data-id="element-17227" />;
      case 'Notification':
        return <BellIcon className="h-5 w-5 text-purple-400" aria-hidden="true" data-id="element-17228" />;
      case 'System':
        return <SettingsIcon className="h-5 w-5 text-gray-400" aria-hidden="true" data-id="element-17229" />;
      default:
        return null;
    }
  };
  const getStyles = () => {
    switch (message.type) {
      case 'Error':
        return {
          bg: 'bg-red-50',
          border: 'border-red-400',
          text: 'text-red-700',
          button: 'text-red-500 hover:bg-red-100 focus:ring-red-500',
          toast: 'bg-red-100 text-red-800'
        };
      case 'Warning':
        return {
          bg: 'bg-amber-50',
          border: 'border-amber-400',
          text: 'text-amber-700',
          button: 'text-amber-500 hover:bg-amber-100 focus:ring-amber-500',
          toast: 'bg-amber-100 text-amber-800'
        };
      case 'Success':
        return {
          bg: 'bg-green-50',
          border: 'border-green-400',
          text: 'text-green-700',
          button: 'text-green-500 hover:bg-green-100 focus:ring-green-500',
          toast: 'bg-green-100 text-green-800'
        };
      case 'Info':
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-400',
          text: 'text-blue-700',
          button: 'text-blue-500 hover:bg-blue-100 focus:ring-blue-500',
          toast: 'bg-blue-100 text-blue-800'
        };
      case 'Notification':
        return {
          bg: 'bg-purple-50',
          border: 'border-purple-400',
          text: 'text-purple-700',
          button: 'text-purple-500 hover:bg-purple-100 focus:ring-purple-500',
          toast: 'bg-purple-100 text-purple-800'
        };
      case 'System':
        return {
          bg: 'bg-gray-50',
          border: 'border-gray-400',
          text: 'text-gray-700',
          button: 'text-gray-500 hover:bg-gray-100 focus:ring-gray-500',
          toast: 'bg-gray-100 text-gray-800'
        };
      default:
        return {
          bg: 'bg-gray-50',
          border: 'border-gray-400',
          text: 'text-gray-700',
          button: 'text-gray-500 hover:bg-gray-100 focus:ring-gray-500',
          toast: 'bg-gray-100 text-gray-800'
        };
    }
  };
  const styles = getStyles();
  if (displayType === 'banner') {
    return <div className={`rounded-md ${styles.bg} p-4 border ${styles.border}`} data-id="element-17230">
        <div className="flex" data-id="element-17231">
          <div className="flex-shrink-0" data-id="element-17232">{getIcon()}</div>
          <div className="ml-3" data-id="element-17233">
            <p className={`text-sm font-medium ${styles.text}`} data-id="element-17234">
              {message.text}
            </p>
          </div>
          <div className="ml-auto pl-3" data-id="element-17235">
            <div className="-mx-1.5 -my-1.5" data-id="element-17236">
              <button type="button" className={`inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2 ${styles.button}`} data-id="element-17237">
                <span className="sr-only" data-id="element-17238">Dismiss</span>
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-17239" />
              </button>
            </div>
          </div>
        </div>
      </div>;
  }
  if (displayType === 'toast') {
    return <div className="max-w-sm w-full shadow-lg rounded-lg pointer-events-auto overflow-hidden" data-id="element-17240">
        <div className={`${styles.toast} p-4`} data-id="element-17241">
          <div className="flex items-start" data-id="element-17242">
            <div className="flex-shrink-0" data-id="element-17243">{getIcon()}</div>
            <div className="ml-3 w-0 flex-1 pt-0.5" data-id="element-17244">
              <p className="text-sm font-medium" data-id="element-17245">{message.text}</p>
            </div>
            <div className="ml-4 flex-shrink-0 flex" data-id="element-17246">
              <button className={`inline-flex rounded-md ${styles.button} focus:outline-none focus:ring-2 focus:ring-offset-2`} data-id="element-17247">
                <span className="sr-only" data-id="element-17248">Close</span>
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-17249" />
              </button>
            </div>
          </div>
        </div>
      </div>;
  }
  // Inline display
  return <div className="flex items-center" data-id="element-17250">
      <div className="flex-shrink-0" data-id="element-17251">{getIcon()}</div>
      <p className={`ml-2 text-sm ${styles.text}`} data-id="element-17252">{message.text}</p>
    </div>;
}